import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

import EmployeeFormModal from './components/EmployeeFormModal';
import {
  ERPPage, ERPPageHeader, ERPKPIGrid, ERPKPICard, ERPActionBar, ERPButton, ERPError,
  ERPTableTh, ERPTableRow, ERPTableTd, ERPBadge, ERPLoading, ERPEmpty, ERPPagination,
  ERPSearchInput, ERPFilterSelect, ERPCard
} from '../../components/ui/ERPLayout';
import { employeeService } from '../../services/employeeService';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ role: 'all', status: 'all' });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => { applyFilters(); }, [filters, employees, search]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await employeeService?.getAll();
      setEmployees(data || []);
    } catch (err) {
      setError(err?.message || 'Error al cargar empleados');
    } finally { setLoading(false); }
  };

  const applyFilters = () => {
    let filtered = [...employees];
    if (search) {
      const s = search?.toLowerCase();
      filtered = filtered?.filter(e =>
        e?.nombre?.toLowerCase()?.includes(s) ||
        e?.rol?.toLowerCase()?.includes(s)
      );
    }
    if (filters?.role !== 'all') filtered = filtered?.filter(e => e?.rol === filters?.role);
    if (filters?.status !== 'all') filtered = filtered?.filter(e => e?.activo === (filters?.status === 'active'));
    setFilteredEmployees(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'reset') { setFilters({ role: 'all', status: 'all' }); setSearch(''); }
    else setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleToggleStatus = async (employee) => {
    try {
      await employeeService?.toggleActive(employee?.id, employee?.activo);
      await loadEmployees();
      if (selectedEmployee?.id === employee?.id) {
        const updated = await employeeService?.getById(employee?.id);
        setSelectedEmployee(updated);
      }
    } catch (err) { setError(err?.message || 'Error al cambiar estado'); }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('¿Eliminar este empleado?')) return;
    try {
      setLoading(true);
      await employeeService?.delete(employeeId);
      await loadEmployees();
      if (selectedEmployee?.id === employeeId) setSelectedEmployee(null);
    } catch (err) { setError(err?.message || 'Error al eliminar empleado'); }
    finally { setLoading(false); }
  };

  const handleSaveEmployee = async (employeeData) => {
    try {
      setLoading(true);
      if (editingEmployee) {
        await employeeService?.update(editingEmployee?.id, {
          nombre: employeeData?.nombre, rol: employeeData?.rol, activo: employeeData?.activo
        });
      } else {
        await employeeService?.create({
          nombre: employeeData?.nombre, rol: employeeData?.rol,
          activo: employeeData?.activo !== undefined ? employeeData?.activo : true
        });
      }
      await loadEmployees();
      setShowFormModal(false);
      setEditingEmployee(null);
    } catch (err) { setError(err?.message || 'Error al guardar empleado'); }
    finally { setLoading(false); }
  };

  const counts = {
    total: employees?.length,
    activos: employees?.filter(e => e?.activo)?.length,
    inactivos: employees?.filter(e => !e?.activo)?.length,
  };

  const kpiCards = [
    { title: 'Empleados Activos', value: counts?.activos, subtitle: 'Trabajando actualmente', icon: 'UserCheck', iconColor: '#3A7F6B', valueColor: '#3A7F6B' },
    { title: 'Empleados Inactivos', value: counts?.inactivos, subtitle: 'Sin actividad', icon: 'UserX', iconColor: '#E05252', valueColor: '#E05252' },
    { title: 'Total Empleados', value: counts?.total, subtitle: 'En el sistema', icon: 'Users', iconColor: '#D6EF4E', valueColor: '#D6EF4E' },
    { title: 'Productividad Media', value: '—', subtitle: 'Rendimiento promedio', icon: 'TrendingUp', iconColor: '#C9A24D', valueColor: '#C9A24D' },
  ];

  // Paginated data
  const totalRecords = filteredEmployees?.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedEmployees = filteredEmployees?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <Helmet><title>Gestión de Empleados - Las Dos Doncellas</title></Helmet>
      <ERPPage>
        {/* Header */}
        <ERPPageHeader
          title="Gestión de Empleados"
          subtitle="Administra tu equipo y monitorea el desempeño"
          actions={
            <>
              <ERPButton variant="primary" icon="UserPlus" onClick={() => { setEditingEmployee(null); setShowFormModal(true); }} loading={false} btnLoading={false} disabled={false}>
                Nuevo Empleado
              </ERPButton>
            </>
          }
        />

        {/* KPIs */}
        <ERPKPIGrid cols={4}>
          {kpiCards?.map(k => <ERPKPICard key={k?.title} {...k} loading={loading} />)}
        </ERPKPIGrid>

        {/* Filter Bar */}
        <ERPActionBar>
          <ERPSearchInput
            value={search}
            onChange={(v) => { setSearch(v); setCurrentPage(1); }}
            placeholder="Buscar empleado..."
          />
          <ERPFilterSelect value={filters?.role} onChange={(v) => handleFilterChange('role', v)}>
            <option value="all">Todos los roles</option>
            <option value="admin">Administrador</option>
            <option value="employee">Empleado</option>
          </ERPFilterSelect>
          <ERPFilterSelect value={filters?.status} onChange={(v) => handleFilterChange('status', v)}>
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </ERPFilterSelect>
          <ERPButton variant="ghost" icon="X" onClick={() => handleFilterChange('reset', null)} size="sm" loading={false} btnLoading={false} disabled={false}>
            Limpiar
          </ERPButton>
        </ERPActionBar>

        {error && <div className="mb-4"><ERPError message={error} onRetry={loadEmployees} /></div>}

        {/* Main layout: table + optional detail panel */}
        <div className={`grid gap-5 ${selectedEmployee ? 'grid-cols-1 xl:grid-cols-[1fr_320px]' : 'grid-cols-1'}`}>
          {/* Full-width Employee Table */}
          <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-oranienbaum">
                <thead>
                  <tr className="border-b border-white/8 bg-[#141414]">
                    <ERPTableTh onClick={() => {}} sortable={true} sorted={false} direction="asc">#</ERPTableTh>
                    <ERPTableTh onClick={() => {}} sortable={true} sorted={false} direction="asc">Empleado</ERPTableTh>
                    <ERPTableTh onClick={() => {}} sortable={true} sorted={false} direction="asc">Rol</ERPTableTh>
                    <ERPTableTh onClick={() => {}} sortable={true} sorted={false} direction="asc">Estado</ERPTableTh>
                    <ERPTableTh onClick={() => {}} sortable={true} sorted={false} direction="asc">Acciones</ERPTableTh>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {loading ? (
                    <tr><td colSpan={5} className="py-16"><ERPLoading /></td></tr>
                  ) : !paginatedEmployees?.length ? (
                    <tr><td colSpan={5} className="py-16"><ERPEmpty message="No se encontraron empleados" /></td></tr>
                  ) : (
                    paginatedEmployees?.map((emp, idx) => {
                      const globalIdx = (currentPage - 1) * pageSize + idx + 1;
                      const isSelected = emp?.id === selectedEmployee?.id;
                      return (
                        <ERPTableRow
                          key={emp?.id}
                          onClick={() => setSelectedEmployee(isSelected ? null : emp)}
                          selected={isSelected ?? false}
                         
                        >
                          <ERPTableTd>
                            <span className="text-white/40 text-xs">{globalIdx}</span>
                          </ERPTableTd>
                          <ERPTableTd>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[#D6EF4E]/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-[#D6EF4E]">
                                  {emp?.nombre?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <span className="font-semibold text-white/90">{emp?.nombre}</span>
                            </div>
                          </ERPTableTd>
                          <ERPTableTd>
                            <ERPBadge color={emp?.rol === 'admin' ? 'yellow' : 'default'}>
                              {emp?.rol === 'admin' ? 'Administrador' : 'Empleado'}
                            </ERPBadge>
                          </ERPTableTd>
                          <ERPTableTd>
                            <ERPBadge color={emp?.activo ? 'green' : 'red'}>
                              {emp?.activo ? 'Activo' : 'Inactivo'}
                            </ERPBadge>
                          </ERPTableTd>
                          <ERPTableTd>
                            <div className="flex items-center gap-1" onClick={(e) => e?.stopPropagation()}>
                              <button
                                onClick={() => { setEditingEmployee(emp); setShowFormModal(true); }}
                                className="p-1.5 rounded-lg text-white/40 hover:text-[#D6EF4E] hover:bg-[#D6EF4E]/8 transition-colors"
                                title="Editar"
                              >
                                <Icon name="Edit" size={13} />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(emp)}
                                className={`p-1.5 rounded-lg transition-colors ${emp?.activo ? 'text-white/40 hover:text-[#E05252] hover:bg-[#E05252]/8' : 'text-white/40 hover:text-[#3A7F6B] hover:bg-[#3A7F6B]/8'}`}
                                title={emp?.activo ? 'Desactivar' : 'Activar'}
                              >
                                <Icon name={emp?.activo ? 'UserX' : 'UserCheck'} size={13} />
                              </button>
                              <button
                                onClick={() => handleDeleteEmployee(emp?.id)}
                                className="p-1.5 rounded-lg text-white/40 hover:text-[#E05252] hover:bg-[#E05252]/8 transition-colors"
                                title="Eliminar"
                              >
                                <Icon name="Trash2" size={13} />
                              </button>
                            </div>
                          </ERPTableTd>
                        </ERPTableRow>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {totalRecords > 0 && (
              <ERPPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                pageSize={pageSize}
                onPageChange={(p) => setCurrentPage(p)}
                onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
              />
            )}
          </div>

          {/* Employee Detail Panel — only shown when employee selected */}
          {selectedEmployee && (
            <ERPCard title="Ficha de Empleado" titleAction={null}>
              <div className="space-y-4">
                {/* Avatar */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#D6EF4E]/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold font-oranienbaum text-[#D6EF4E]">
                      {selectedEmployee?.nombre?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold font-oranienbaum text-white">{selectedEmployee?.nombre}</p>
                    <p className="text-xs font-oranienbaum text-white/40 capitalize">{selectedEmployee?.rol}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs font-oranienbaum text-white/50">Estado</span>
                  <ERPBadge color={selectedEmployee?.activo ? 'green' : 'red'}>
                    {selectedEmployee?.activo ? 'Activo' : 'Inactivo'}
                  </ERPBadge>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs font-oranienbaum text-white/50">Rol</span>
                  <span className="text-sm font-oranienbaum text-white/80 capitalize">{selectedEmployee?.rol}</span>
                </div>

                {selectedEmployee?.email && (
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-xs font-oranienbaum text-white/50">Email</span>
                    <span className="text-xs font-oranienbaum text-white/70 truncate max-w-[180px]">{selectedEmployee?.email}</span>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <ERPButton
                    variant="secondary" icon="Edit" size="sm" className="flex-1"
                    onClick={() => { setEditingEmployee(selectedEmployee); setShowFormModal(true); }}
                    loading={false}
                    btnLoading={false}
                    disabled={false}
                   
                  >
                    Editar
                  </ERPButton>
                  <ERPButton
                    variant={selectedEmployee?.activo ? 'danger' : 'primary'}
                    icon={selectedEmployee?.activo ? 'UserX' : 'UserCheck'}
                    size="sm" className="flex-1"
                    onClick={() => handleToggleStatus(selectedEmployee)}
                    loading={false}
                    btnLoading={false}
                    disabled={false}
                   
                  >
                    {selectedEmployee?.activo ? 'Desactivar' : 'Activar'}
                  </ERPButton>
                </div>
                <ERPButton
                  variant="ghost" icon="X" size="sm" className="w-full"
                  onClick={() => setSelectedEmployee(null)}
                  loading={false}
                  btnLoading={false}
                  disabled={false}
                 
                >
                  Cerrar
                </ERPButton>
              </div>
            </ERPCard>
          )}
        </div>
      </ERPPage>

      {showFormModal && (
        <EmployeeFormModal
          employee={editingEmployee}
          onSave={handleSaveEmployee}
          onClose={() => { setShowFormModal(false); setEditingEmployee(null); }}
          isOpen={showFormModal}
        />
      )}
    </>
  );
};

export default EmployeeManagement;